import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { camelize } from '../../util/string';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
} from '../../util/types';
import { languagesHelper as language } from '../../util/languages';
import { getLocalizedRewildingLandscapes } from '../../util/cmsPageHelper';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import { createResourceLocatorString } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  INQUIRY_PROCESS_NAME,
  NEGOTIATION_PROCESS_NAME,
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  IconShare,
  AddToFavouritesButton,
  Modal,
  Button,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  sendAfterHoldingPeriod,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmitAfterHoldingPeriod,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
  getCanonicalListingRedirect,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import TranslationNotice, { TRANSLATION_NOTICE_CLOSED_KEY } from './TranslationNotice';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import SectionProgram from './SectionProgram';
import SectionPlaceReviews from './SectionPlaceReviews.js';
import CustomListingFields from './CustomListingFields';
import SectionMainInfo from './SectionMainInfo';
import SectionDescription from './SectionDescription';
import SectionFoodAndStayGallery from './SectionFoodAndStayGallery';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import SectionSeasonalAvailability from './SectionSeasonalAvailability';
import SectionRewildingEfforts from './SectionRewildingEfforts';
import SectionFAQ from './SectionFAQ';
import SectionExperienceInvolves from './SectionExperienceInvolves';
import SectionLandscapeWildlife from './SectionLandscapeWildlife';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const STRIPE_HOLDING_PERIOD = 90;

const { UUID } = sdkTypes;

const formatDurationInMinutes = (durationInMinutes, intl) => {
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(intl.formatMessage({ id: 'ListingPage.durationHours' }, { count: hours }));
  }
  if (minutes > 0) {
    parts.push(intl.formatMessage({ id: 'ListingPage.durationMinutes' }, { count: minutes }));
  }

  return parts.join(', ');
};

const getSessionDurationFromPriceVariants = (priceVariants, intl) => {
  if (!Array.isArray(priceVariants) || priceVariants.length === 0) {
    return null;
  }

  const lengths = priceVariants
    .map(variant => variant?.bookingLengthInMinutes)
    .filter(minutes => typeof minutes === 'number' && minutes > 0);

  if (lengths.length === 0) {
    return null;
  }

  const uniqueLengths = [...new Set(lengths)];
  const minLength = Math.min(...lengths);
  const formatted = formatDurationInMinutes(minLength, intl);

  if (!formatted) {
    return null;
  }

  return uniqueLengths.length > 1
    ? intl.formatMessage({ id: 'ListingPage.durationFrom' }, { duration: formatted })
    : formatted;
};

const getDurationMainInfoField = (publicData, intl) => {
  const isDaytimeExperience = publicData?.categoryLevel1 === 'category-experiences';

  if (isDaytimeExperience) {
    const sessionDuration = getSessionDurationFromPriceVariants(publicData?.priceVariants, intl);
    if (sessionDuration) {
      return {
        key: 'field_duration',
        value: sessionDuration,
        label: 'ListingPage.duration',
        durationUnit: 'session',
      };
    }
  }

  return {
    key: 'field_duration',
    value: publicData?.field_duration,
    label: 'ListingPage.duration',
    durationUnit: 'days',
  };
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [isFafouriteModalOpen, setIsFafouriteModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isVisibleNotice, setIsVisibleNotice] = useState(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return true;
    }
    return window.localStorage.getItem(TRANSLATION_NOTICE_CLOSED_KEY) !== 'true';
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onSendAfterHoldingPeriod,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    lineTotal,
    placeReviews,
    fetchPlaceReviewsError,
    pageAssetsData,
    pageTranslations,
    listingTranslation,
    providerTranslation,
    ...restOfProps
  } = props;

  const locale = language.get();
  const landingPageData = pageAssetsData?.[camelize('landing-page')]?.data;
  const rewildingLandscapes = getLocalizedRewildingLandscapes(
    landingPageData,
    pageTranslations,
    locale
  );

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const canonicalListingRedirect = getCanonicalListingRedirect({
    rawParams,
    params,
    location,
    staticContext: props.staticContext,
  });
  if (canonicalListingRedirect) {
    return canonicalListingRedirect;
  }

  const {
    geolocation = null,
    price = null,

    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const title = listingTranslation[`title_locale_${locale}`] || currentListing.attributes.title;
  const description =
    listingTranslation[`description_locale_${locale}`] || currentListing.attributes.description;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = {
    params,
    history,
    routes: routeConfiguration,
    lineTotal,
    currency: config.currency,
  };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmitAfterHoldingPeriod = handleSubmitAfterHoldingPeriod({
    ...commonParams,
    currentUser,
    getListing,
    onSendAfterHoldingPeriod,
    isAuthenticated,
    location,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    currentUser,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const getStartDate = values => {
    if (values.bookingDates) {
      return values.bookingDates.startDate;
    }

    if (values.bookingStartDate) {
      return values.bookingStartDate.date;
    }

    return null;
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      if (Object.keys(values).length === 0) {
        onSubmit(values);
        return;
      }

      const startDate = getStartDate(values);
      const today = moment().startOf('day');
      const bookingDate = moment(startDate).startOf('day');
      const daysUntilBooking = bookingDate.diff(today, 'days');

      if (daysUntilBooking > STRIPE_HOLDING_PERIOD) {
        onSubmitAfterHoldingPeriod(values);
      } else {
        onSubmit(values);
      }
    }
  };

  const onOpenFafouriteModal = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsFafouriteModalOpen(true);
  };

  const handleAddToFavouritesUnlogged = type => {
    const state = {
      from: `${location.pathname}${location.search}${location.hash}`,
    };
    const page = type === 'signup' ? 'SignupPage' : 'LoginPage';

    // Sign up and return back to the listing page.
    history.push(createResourceLocatorString(page, routeConfiguration, {}, {}), state);
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const mainInfoFields = [
    getDurationMainInfoField(publicData, intl),
    {
      key: 'field_difficulty',
      value: publicData?.field_difficulty,
      label: 'ListingPage.difficulty',
    },
    { key: 'field_groupsize', value: publicData?.field_groupsize, label: 'ListingPage.group' },
    { key: 'field_age', value: publicData?.field_age, label: 'ListingPage.age' },
    {
      key: 'listingField_languages',
      value: publicData?.listingField_languages,
      label: 'ListingPage.language',
    },
  ];

  const mainInfoFieldsIds = mainInfoFields.map(f => f.key);
  const disableFields = [
    'listingFieldID_program',
    'field_itinerary',
    'field_included',
    'field_whattobring',
    'field_foodstay',
    'videoexperience',
    'videostay',
    'field_pickups_and_drop_offs',
    'listing_rewildingsuppportstay',
    'listing_rewildingsuppportexp',
    'listingfield_shortDescription',
    'Field_datesduration',
    'field_faq',
    'faq',
    'field_season',
    'Thisexperienceinvolves',
    'Herbivoresinthislandscape',
    'Carnivoresinthislandscape',
    'Largebirdsinthislandscape',
    'Fishcrayfishmolluscs',
    ...mainInfoFieldsIds,
  ];

  const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  let pathname = `${rootUrl}/l/${listingSlug}/${listingId.uuid}`;

  const copyLink = () => {
    if (typeof window === undefined) {
      return;
    }
    window.navigator.clipboard.writeText(pathname);

    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const actionButtons = (
    <div className={css.actionButtons}>
      <div className={css.shareBtn} onClick={copyLink}>
        <IconShare />
      </div>

      {isCopied && (
        <div className={css.shareTooltip}>
          <FormattedMessage id="ListingPage.copiedToClipboard" />
        </div>
      )}

      <AddToFavouritesButton
        listing={currentListing}
        className={css.addToFavourites}
        onOpenFafouriteModal={onOpenFafouriteModal}
      />
    </div>
  );

  const getTextContent = name => {
    return listingTranslation?.[`${name}_locale_${locale}`] || publicData?.[name];
  };

  const dismissTranslationNotice = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(TRANSLATION_NOTICE_CLOSED_KEY, 'true');
    }
    setIsVisibleNotice(false);
  };

  const translationNotice = (
    <TranslationNotice isVisible={isVisibleNotice} onDismiss={dismissTranslationNotice} />
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        className={classNames(css.pageRoot, isVisibleNotice && css.pageRootWithNotice)}
        topbar={topbar}
        footer={<FooterContainer />}
      >
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            <div className={css.productGalleryWrapper}>
              <div className={css.actionBarWrapper}>
                {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                    currentUser={currentUser}
                  />
                ) : null}
                {mounted && currentListing.id ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    currentUser={currentUser}
                    editParams={{
                      id: listingId.uuid,
                      slug: listingSlug,
                      type: listingPathParamType,
                      tab: listingTab,
                    }}
                  />
                ) : null}
              </div>
              {showListingImage && (
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                  publicData={publicData}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              )}
              <div className={classNames(css.titleWrapper, css.titleWrapperDesktop)}>
                <h1 className={css.title}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </h1>
                {publicData?.listingfield_shortDescription && (
                  <p className={css.shortDescription}>
                    {getTextContent('listingfield_shortDescription')}
                  </p>
                )}
              </div>
            </div>

            <SectionMainInfo
              listing={currentListing}
              reviews={reviews}
              config={config}
              mainInfoFields={mainInfoFields}
              locale={locale}
              listingTranslation={listingTranslation}
            />

            <div className={css.actionButtonsMobile}>{actionButtons}</div>

            <div className={classNames(css.titleWrapper, css.titleWrapperMobile)}>
              <h1 className={css.title}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </h1>
              {publicData?.listingfield_shortDescription && (
                <p className={css.shortDescription}>
                  {getTextContent('listingfield_shortDescription')}
                </p>
              )}
            </div>

            <div className={css.infoWrapper}>
              <SectionDescription
                name="description"
                title={<FormattedMessage id="ListingPage.descriptionTitle" />}
                description={description}
                publicData={publicData}
                locale={locale}
                hideAccordionTitle
              />
              <SectionProgram
                name="listingFieldID_program"
                title={<FormattedMessage id="ListingPage.program" />}
                description={getTextContent('listingFieldID_program')}
                publicData={publicData}
                locale={locale}
                listingTranslation={listingTranslation}
              />
              {!publicData?.programDay && (
                <SectionDescription
                  name="field_itinerary"
                  title={<FormattedMessage id="ListingPage.itineraryTitle" />}
                  description={getTextContent('field_itinerary')}
                />
              )}
              <SectionDescription
                name="field_included"
                title={<FormattedMessage id="ListingPage.whatsIncluded" />}
                description={getTextContent('field_included')}
              />
              <SectionDescription
                name="field_whattobring"
                title={<FormattedMessage id="ListingPage.whatToBring" />}
                description={getTextContent('field_whattobring')}
              />
              <SectionFoodAndStayGallery
                name="field_foodstay"
                title={<FormattedMessage id="ListingPage.foodAndStay" />}
                description={getTextContent('field_foodstay')}
                publicData={publicData}
                locale={locale}
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
                onManageDisableScrolling={onManageDisableScrolling}
              />
              <SectionYoutubeVideoMaybe
                heading={
                  <FormattedMessage
                    id={
                      publicData?.videoexperience
                        ? 'ListingPage.experienceVideoTitle'
                        : 'ListingPage.stayVideoTitle'
                    }
                  />
                }
                videoUrl={publicData?.videoexperience || publicData?.videostay}
              />

              <SectionDescription
                name="field_pickups_and_drop_offs"
                title={<FormattedMessage id="ListingPage.pickupsAndDropoffs" />}
                description={getTextContent('field_pickups_and_drop_offs')}
              />

              <SectionRewildingEfforts
                publicData={publicData}
                locale={locale}
                rewildingLandscapes={rewildingLandscapes}
                listingTranslation={listingTranslation}
              />

              <SectionSeasonalAvailability
                title={<FormattedMessage id="ListingPage.seasonalAvailability" />}
                publicData={publicData}
                config={config}
              />

              <SectionFAQ
                publicData={publicData}
                locale={locale}
                listingTranslation={listingTranslation}
              />

              <SectionExperienceInvolves
                publicData={publicData}
                locale={locale}
                listingFieldConfigs={listingConfig.listingFields}
              />

              <SectionLandscapeWildlife
                publicData={publicData}
                locale={locale}
                listingFieldConfigs={listingConfig.listingFields}
              />

              <CustomListingFields
                listing={currentListing}
                publicData={publicData}
                metadata={metadata}
                listingFieldConfigs={listingConfig.listingFields}
                categoryConfiguration={config.categoryConfiguration}
                intl={intl}
                locale={locale}
                variantPrefix={config.layout.listingImage.variantPrefix}
                disableFields={disableFields}
                propertyGroupClass={css.propertyGroupList}
                listingTranslation={listingTranslation}
              />

              <SectionMapMaybe
                geolocation={geolocation}
                publicData={publicData}
                listingId={currentListing.id}
                mapsConfig={config.maps}
              />

              <SectionReviews
                reviews={reviews}
                fetchReviewsError={fetchReviewsError}
                locale={locale}
              />

              <SectionPlaceReviews
                publicData={publicData}
                placeReviews={placeReviews}
                fetchPlaceReviewsError={fetchPlaceReviewsError}
              />

              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={classNames(css.productOrderPanel, {
                [css.imagesEnabled]: showListingImage,
              })}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
              showContactProvider={
                processName !== INQUIRY_PROCESS_NAME && processName !== NEGOTIATION_PROCESS_NAME
              }
              actionButtons={actionButtons}
              isListingPage={true}
              onOpenFafouriteModal={onOpenFafouriteModal}
              translationNotice={translationNotice}
            />
          </div>
        </div>

        <Modal
          id="FavouritesPopup"
          isOpen={isFafouriteModalOpen}
          onClose={() => setIsFafouriteModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.favouritesPopup}>
            <p className={css.favouritesPopupDescription}>
              <FormattedMessage id="FavouriteListingsPopup.description" />
            </p>
            <div className={css.favouritesPopupButtons}>
              <Button
                type="button"
                className={css.favouritesPopupButton}
                onClick={() => handleAddToFavouritesUnlogged('login')}
              >
                <FormattedMessage id="FavouriteListingsPopup.login" />
              </Button>
              <Button
                type="button"
                className={css.favouritesPopupButton}
                onClick={() => handleAddToFavouritesUnlogged('signup')}
              >
                <FormattedMessage id="FavouriteListingsPopup.signup" />
              </Button>
            </div>
          </div>
        </Modal>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  const { loadingListingTranslation, loadingProviderTranslation } = props;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  if (loadingListingTranslation || loadingProviderTranslation) {
    return null;
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    placeReviews,
    fetchPlaceReviewsError,
    listingTranslation,
    loadingListingTranslation,
    providerTranslation,
    loadingProviderTranslation,
    externalServicesEvents,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const { pageAssetsData } = state.hostedAssets || {};
  const { pageTranslations } = state.EditTranslationsPage || {};

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getLineTotal = lineItems => {
    const foundLineItem = lineItems.find(
      item =>
        item.code === LINE_ITEM_NIGHT ||
        item.code === LINE_ITEM_DAY ||
        item.code === LINE_ITEM_HOUR ||
        item.code === LINE_ITEM_FIXED ||
        item.code === LINE_ITEM_ITEM
    );

    if (foundLineItem) {
      return foundLineItem.lineTotal.amount / 100;
    } else {
      return 0;
    }
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    lineTotal: !lineItems ? 0 : getLineTotal(lineItems),
    placeReviews,
    fetchPlaceReviewsError,
    pageAssetsData,
    pageTranslations,
    listingTranslation,
    loadingListingTranslation,
    providerTranslation,
    loadingProviderTranslation,
    externalServicesEvents,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onSendAfterHoldingPeriod: params => dispatch(sendAfterHoldingPeriod(params)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
